// eslint-disable-next-line @typescript-eslint/no-unused-vars
import React, { useState, useEffect, useMemo } from 'react';
import './index.css';
import { ApprovalRequest } from '../../../../../redux/AllocationRequest';
import { ApprovalCard } from '../ApprovalCard';
import { useOrderRequests, OrderRequest } from '../../../../../hooks/useOrderRequest';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Spinner, Alert, Row, Col, Form, Tabs, Tab, Badge } from 'react-bootstrap';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { Calendar, Filter, X, CheckCircle, XCircle, Clock, List } from 'lucide-react';
import { useAcceptOrderRequestMutation } from '../../../../../redux/services/allocationService';
import { toast } from 'react-toastify';

export const ApprovalsTab: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showDateFilter, setShowDateFilter] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('all');
  const [processingRequestId, setProcessingRequestId] = useState<string | null>(null);

  const {
    requests: firebaseRequests,
    loading,
    error,
  } = useOrderRequests({
    realtime: true,
  });

  const [acceptOrderRequest, { isLoading: isAccepting }] = useAcceptOrderRequestMutation();

  // ── Date helpers ─────────────────────────────────────────────────────────────
  const formatToDateOnly = (dateValue: any): string => {
    if (!dateValue) return '';
    try {
      let date: Date;
      if (dateValue && typeof dateValue === 'object') {
        if ('seconds' in dateValue && typeof dateValue.seconds === 'number') {
          date = new Date(dateValue.seconds * 1000);
        } else if (typeof dateValue.toDate === 'function') {
          date = dateValue.toDate();
        } else if (dateValue instanceof Date) {
          date = dateValue;
        } else {
          date = new Date(dateValue);
        }
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else {
        date = new Date();
      }
      if (isNaN(date.getTime())) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    } catch {
      return '';
    }
  };

  const toISOString = (dateValue: any): string => {
    if (!dateValue) return new Date().toISOString();
    try {
      let date: Date;
      if (dateValue && typeof dateValue === 'object') {
        if ('seconds' in dateValue && typeof dateValue.seconds === 'number') {
          date = new Date(dateValue.seconds * 1000);
        } else if (typeof dateValue.toDate === 'function') {
          date = dateValue.toDate();
        } else if (dateValue instanceof Date) {
          date = dateValue;
        } else {
          date = new Date(dateValue);
        }
      } else if (typeof dateValue === 'string') {
        date = new Date(dateValue);
      } else if (typeof dateValue === 'number') {
        date = new Date(dateValue);
      } else {
        date = new Date();
      }
      if (isNaN(date.getTime())) return new Date().toISOString();
      return date.toISOString();
    } catch {
      return new Date().toISOString();
    }
  };

  // ── Transform ─────────────────────────────────────────────────────────────────
  const transformToApprovalRequest = (req: OrderRequest): ApprovalRequest & {
    cartAllocations?: any[];
    requestType?: string;
  } => {
    const formattedTargetDate = formatToDateOnly(req.targetDate);

    // Normalize cartAllocations — Firestore may store as array or map
    const rawCartAllocations = (req as any).cartAllocations;
    let cartAllocations: any[] = [];

    if (Array.isArray(rawCartAllocations)) {
      cartAllocations = rawCartAllocations;
    } else if (rawCartAllocations && typeof rawCartAllocations === 'object') {
      // Firestore map — convert to array
      cartAllocations = Object.values(rawCartAllocations);
    }

    // Normalize each cart's allocations field (also could be map in Firestore)
    cartAllocations = cartAllocations.map((cart: any) => {
      let allocations = cart.allocations || [];
      if (!Array.isArray(allocations) && typeof allocations === 'object') {
        allocations = Object.values(allocations);
      }
      return { ...cart, allocations };
    });

    return {
      id: req.id,
      type: req.requestType as any,
      requestType: req.requestType,
      department: req.department,
      requester: req.requestedBy,
      summary: `${req.totalItems} product(s), ${req.totalQuantity} units`,
      totalItems: req.totalItems,
      totalUnits: req.totalQuantity,
      notes: req.notes || '',
      targetDate: formattedTargetDate,
      products: req.products?.map(p => ({
        name: p.displayName,
        tier: p.tier as any,
        quantity: p.quantity,
        skuId: p.skuId,
        price: p.price,
        itemType: p.itemType,
        customItems: (p as any).customItems,
      })) || [],
      status: req.status.toLowerCase() as any,
      createdAt: toISOString(req.createdAt),
      cartAllocations,
    };
  };

  // ── Filtered requests ─────────────────────────────────────────────────────────
  const filteredRequests = useMemo(() => {
    let filtered = firebaseRequests.map(transformToApprovalRequest);

    if (selectedDate) {
      filtered = filtered.filter(req => {
        const requestDate = formatToDateOnly(req.targetDate);
        return requestDate === selectedDate;
      });
    }

    switch (activeTab) {
      case 'pending':  return filtered.filter(req => req.status === 'pending');
      case 'approved': return filtered.filter(req => req.status === 'approved');
      case 'rejected': return filtered.filter(req => req.status === 'rejected');
      default: return filtered;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseRequests, selectedDate, activeTab]);

  // ── Stats ─────────────────────────────────────────────────────────────────────
  const filteredStats = useMemo(() => {
    const dateFiltered = firebaseRequests
      .map(transformToApprovalRequest)
      .filter(req => formatToDateOnly(req.targetDate) === selectedDate);

    return {
      pending:  dateFiltered.filter(r => r.status === 'pending').length,
      approved: dateFiltered.filter(r => r.status === 'approved').length,
      rejected: dateFiltered.filter(r => r.status === 'rejected').length,
      total:    dateFiltered.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseRequests, selectedDate]);

  // ── Handlers ──────────────────────────────────────────────────────────────────
  const handleApprove = async (request: ApprovalRequest, notes?: string) => {
    try {
      setProcessingRequestId(request.id);
      const originalRequest = firebaseRequests.find(r => r.id === request.id);
      if (!originalRequest) throw new Error('Original request not found');

      await acceptOrderRequest({
        orderRequestId: request.id,
        requestStatus: 'Approved',
        notes: notes || originalRequest.notes,
      }).unwrap();

      toast.success('Request approved successfully');
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to approve request';
      toast.error(errorMessage);
    } finally {
      setProcessingRequestId(null);
    }
  };

  const handleReject = async (request: ApprovalRequest, notes?: string) => {
    try {
      setProcessingRequestId(request.id);
      const originalRequest = firebaseRequests.find(r => r.id === request.id);
      if (!originalRequest) throw new Error('Original request not found');

      await acceptOrderRequest({
        orderRequestId: request.id,
        requestStatus: 'Rejected',
        notes: notes || 'No reason provided',
      }).unwrap();

      toast.success('Request rejected successfully');
    } catch (error: any) {
      const errorMessage = error?.data?.message || error?.message || 'Failed to reject request';
      toast.error(errorMessage);
    } finally {
      setProcessingRequestId(null);
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDateChange = (date: string) => setSelectedDate(date);

  const handleClearDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    setSelectedDate(`${year}-${month}-${day}`);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const toggleDateFilter = () => setShowDateFilter(p => !p);

  const getTabIcon = (status: string) => {
    switch (status) {
      case 'pending':  return <Clock size={16} className="me-2" />;
      case 'approved': return <CheckCircle size={16} className="me-2" />;
      case 'rejected': return <XCircle size={16} className="me-2" />;
      default:         return <List size={16} className="me-2" />;
    }
  };

  // ── Loading / Error states ────────────────────────────────────────────────────
  if (loading && firebaseRequests.length === 0) {
    return (
      <div className="approvals-tab">
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3 text-muted">Loading approval requests...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="approvals-tab">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Approvals</Alert.Heading>
          <p>{error.message}</p>
        </Alert>
      </div>
    );
  }

  return (
    <div className="approvals-tab">
      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || 'all')}
        className="mb-4"
        fill
      >
        <Tab
          eventKey="all"
          title={
            <span>
              {getTabIcon('all')}All Requests
              <Badge bg="secondary" className="ms-2">{filteredStats.total}</Badge>
            </span>
          }
        />
        <Tab
          eventKey="pending"
          title={
            <span>
              {getTabIcon('pending')}Pending
              <Badge bg="warning" className="ms-2">{filteredStats.pending}</Badge>
            </span>
          }
        />
        <Tab
          eventKey="approved"
          title={
            <span>
              {getTabIcon('approved')}Approved
              <Badge bg="success" className="ms-2">{filteredStats.approved}</Badge>
            </span>
          }
        />
        <Tab
          eventKey="rejected"
          title={
            <span>
              {getTabIcon('rejected')}Rejected
              <Badge bg="danger" className="ms-2">{filteredStats.rejected}</Badge>
            </span>
          }
        />
      </Tabs>

      {/* Requests list */}
      <div className="requests-list">
        {filteredRequests.length === 0 ? (
          <div className="empty-state text-center py-5">
            <div className="empty-state-icon mb-3">
              <Calendar size={48} className="text-muted" />
            </div>
            <h5 className="text-muted">No requests found</h5>
            <p className="text-muted small">
              {activeTab === 'all'
                ? `No requests with target date ${selectedDate}`
                : `No ${activeTab} requests with target date ${selectedDate}`}
            </p>
            {activeTab !== 'all' && (
              <button className="btn btn-outline-primary btn-sm mt-2" onClick={() => setActiveTab('all')}>
                View all requests
              </button>
            )}
            {selectedDate && (
              <button className="btn btn-outline-secondary btn-sm mt-2 ms-2" onClick={handleClearDate}>
                View current date requests
              </button>
            )}
          </div>
        ) : (
          <>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h6 className="mb-0">Showing {filteredRequests.length} request(s)</h6>
              <small className="text-muted">Target Date: {selectedDate}</small>
            </div>
            {filteredRequests.map(request => (
              <ApprovalCard
                key={request.id}
                request={request as any}
                onApprove={handleApprove}
                onReject={handleReject}
                isProcessing={processingRequestId === request.id}
              />
            ))}
          </>
        )}
      </div>

      {/* Processing overlay */}
      {isAccepting && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50"
          style={{ zIndex: 9999 }}
        >
          <div className="bg-white p-4 rounded shadow text-center">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2 mb-0">Processing request...</p>
          </div>
        </div>
      )}
    </div>
  );
};